// ── n8n streaming-response reader ─────────────────────────────────────────────
// Transport: n8n Webhook "Response Mode: Streaming" sends chunked HTTP where each
// line is a structured chunk:
//   {"type":"begin"|"item"|"end"|"error","content"?:string,"metadata":{nodeName,...}}
// App protocol (see docs/StepProductLinq/N8N_STREAMING_BACKEND_INSTRUCTIONS.md): an `item` chunk
// whose content parses to a JSON object with a `kind` key is an app event
// (status / final / error); any other `item` content is raw agent token text.

import { normaliseCode } from './stepErrors';

export type StreamEvent =
  | { kind: 'status'; stage: string; label: string; detail?: string }
  | { kind: 'token'; text: string }
  | { kind: 'final'; output: string; json?: unknown; hints?: string[] }
  | { kind: 'error'; code?: string; message: string };

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// Buffered JSON (the legacy contract) announces itself with a Content-Length.
// n8n 2.x streaming mode sends chunked transfer with NO Content-Length — and
// (verified against n8n 2.18.7) still labels it plain `application/json`, so the
// content type alone cannot distinguish the two. A buffered body that slips
// through (proxy stripped the length) is caught by readN8nStream's fallback.
export const isStreamingResponse = (res: Response): boolean => {
  if (!res.body) return false;
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('json-lines') || ct.includes('event-stream') || ct.includes('jsonl') || ct.includes('ndjson')) return true;
  return res.headers.get('content-length') === null;
};

// App-event coercers — tolerate missing optional fields, reject wrong shapes.

const asStatus = (o: Record<string, unknown>): StreamEvent | null =>
  typeof o.stage === 'string' && typeof o.label === 'string'
    ? { kind: 'status', stage: o.stage, label: o.label, ...(typeof o.detail === 'string' && { detail: o.detail }) }
    : null;

const asFinal = (o: Record<string, unknown>): StreamEvent => ({
  kind: 'final',
  output: typeof o.output === 'string' ? o.output : '',
  ...(o.json !== undefined && { json: o.json }),
  ...(Array.isArray(o.hints) && { hints: o.hints.filter((h): h is string => typeof h === 'string').slice(0, 4) }),
});

// `code` is normalised to UPPER_SNAKE (the 2026-08-03 contract) so a workflow
// still emitting the old lower_snake casing keeps matching STEP_ERROR.
const asError = (o: Record<string, unknown>): StreamEvent => {
  const code = normaliseCode(o.code);
  return {
    kind: 'error',
    message: typeof o.message === 'string' ? o.message : 'Workflow reported an error.',
    ...(code && { code }),
  };
};

// One line of the stream → zero or one StreamEvent.
const routeLine = (line: string, onEvent: (e: StreamEvent) => void): boolean => {
  let chunk: unknown;
  try { chunk = JSON.parse(line); } catch { return false; } // not a structured chunk
  if (!isObj(chunk) || typeof chunk.type !== 'string') return false;

  const content = typeof chunk.content === 'string' ? chunk.content : '';
  if (chunk.type === 'error') {
    onEvent({ kind: 'error', message: content || 'Workflow stream error.' });
    return true;
  }
  if (chunk.type !== 'item') return true; // begin/end — lifecycle only, ignore

  // App event? Content must be a JSON *object* carrying `kind` (guards against
  // agent tokens that happen to be valid JSON like "1" or "[…]").
  const meta = isObj(chunk.metadata) ? chunk.metadata : undefined;
  const nodeName = typeof meta?.nodeName === 'string' ? meta.nodeName : '';
  const looksAppNode = nodeName.startsWith('Status:');
  try {
    const app = JSON.parse(content);
    if (isObj(app) && typeof app.kind === 'string') {
      if (app.kind === 'status') { const e = asStatus(app); if (e) { onEvent(e); return true; } }
      if (app.kind === 'final') { onEvent(asFinal(app)); return true; }
      if (app.kind === 'error') { onEvent(asError(app)); return true; }
      // Unknown kind from a status node — ignore; from the agent — treat as token.
      if (looksAppNode) return true;
    }
  } catch { /* plain token text */ }

  if (content) onEvent({ kind: 'token', text: content });
  return true;
};

// Read the streamed body, emitting StreamEvents as chunks arrive.
// Handles partial lines across network reads. If the body turns out not to be
// structured chunks at all (e.g. a buffered legacy JSON reply that slipped past
// the content-type check), falls back to emitting one synthetic `final` built
// with the same field tolerance as the legacy parser.
export const readN8nStream = async (res: Response, onEvent: (e: StreamEvent) => void): Promise<void> => {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let sawChunk = false;
  let raw = ''; // full body, kept only until we know it's a real chunk stream

  const handleLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    if (routeLine(trimmed, onEvent)) sawChunk = true;
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const text = decoder.decode(value, { stream: true });
    if (!sawChunk) raw += text;
    buffer += text;
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? ''; // keep trailing partial line
    for (const line of lines) handleLine(line);
    if (sawChunk) raw = '';
  }
  buffer += decoder.decode(); // flush decoder + trailing line
  if (!sawChunk) raw += decoder.decode();
  handleLine(buffer);

  if (sawChunk) return;

  // Fallback: whole body wasn't chunked — apply the legacy contract.
  let body: unknown;
  try { body = JSON.parse(raw); } catch { body = undefined; }
  const root = Array.isArray(body) && body.length > 0 ? body[0] : body;
  if (isObj(root)) {
    onEvent(asFinal(root));
  } else {
    onEvent({ kind: 'final', output: typeof root === 'string' ? root : raw.trim() });
  }
};
